package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.agent.RisponditoreDomande.formattaGiorno
import com.heelion.farmafreelance.agent.RisponditoreDomande.plurale
import com.heelion.farmafreelance.models.AnnuncioStato
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.Match
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.Professione
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.etichetta
import com.heelion.farmafreelance.voice.VoiceCommandParser
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

/**
 * Domande sul calendario riconosciute con regole: "il turno di sabato è coperto?", "quali turni ho
 * pubblicato?", "sono libero sabato?", "ci sono turni nel weekend?", "ci sono proposte da infermieri?".
 */
object DomandeCalendario {
    // Chiedere di FARE qualcosa non è una domanda ("crea un turno sabato", "cancella il turno di sabato").
    private val AZIONE = Regex("\\b(crea|creami|segna|aggiung|cancell|elimin|modific|spost|accett|rifiut|prend|disdi|propon|invit|avvisa|ricorda)\\w*")
    // "coperto", non "scoperto": "turni scoperti stasera" sono le emergenze.
    private val COPERTO = Regex("\\bcopert[oaie]\\b|\\bchi (viene|va|lavora|copre|c e)\\b|\\bho (trovato )?qualcuno\\b")
    private val PUBBLICATI = Regex(
        "\\b(quali|che|quanti|quante|mostrami|fammi vedere|elenca)\\b.*\\b(ho|abbiamo) (gia )?(pubblicat|mess)\\w*" +
            "|\\bi (miei|nostri) (annunci|turni pubblicati|turni aperti|turni in bacheca)\\b|\\bturni (che ho|che abbiamo) pubblicat"
    )
    // "lavoro sabato?" sì, "cerco lavoro sabato" no (quella è una ricerca).
    private val LIBERO = Regex(
        "\\bsono liber[oa]\\b|\\bsono impegnat[oa]\\b|\\bho (impegni|un turno|turni|qualcosa da fare)\\b|\\bdevo lavorare\\b" +
            "|^(oggi |domani |dopodomani |sabato |domenica |ma )?lavoro\\b(?! (in|a|come|per|da))"
    )
    private val TURNI_APERTI = Regex("\\b(ci sono|ce|c sono|quali|che|mostrami|trovami|cerco|cerca)\\b.*\\bturn[oi]\\b")
    private val PROPOSTE = Regex("\\b(propost\\w*|candidatur\\w*|richiest\\w*)\\b.*\\b(infermier\\w*|farmacist\\w*)|\\b(infermier\\w*|farmacist\\w*)\\b.*\\bpropost")

    fun tipo(testo: String, oggi: LocalDate): TipoDomanda? {
        val t = IntentClassifier.normalizza(testo)
        if (AZIONE.containsMatchIn(t)) return null
        val quando = EstrattorePeriodo.estrai(testo, oggi) != null || VoiceCommandParser.dataMenzionata(testo, oggi) != null
        return when {
            PROPOSTE.containsMatchIn(t) -> TipoDomanda.STATO_TURNI
            COPERTO.containsMatchIn(t) -> TipoDomanda.TURNO_COPERTO
            PUBBLICATI.containsMatchIn(t) -> TipoDomanda.TURNI_PUBBLICATI
            // "sono libero?" senza giorno: Farmy chiede quale.
            t.contains("sono liber") || (LIBERO.containsMatchIn(t) && quando) -> TipoDomanda.SONO_LIBERO
            TURNI_APERTI.containsMatchIn(t) && quando -> TipoDomanda.TURNI_DISPONIBILI
            else -> null
        }
    }

    /** Il periodo della domanda: un periodo detto, un giorno, o [predefinito] giorni da oggi. */
    fun intervallo(testo: String, oggi: LocalDate, predefinito: Int? = null): PeriodoRicerca? {
        EstrattorePeriodo.estrai(testo, oggi)?.let { return it }
        VoiceCommandParser.dataMenzionata(testo, oggi)?.let { return PeriodoRicerca(it, it, formattaGiorno(it, oggi)) }
        return predefinito?.let { PeriodoRicerca(oggi, oggi.plus(it - 1, DateTimeUnit.DAY), "nei prossimi $it giorni") }
    }

    /** "inf-12" / "smart-12" -> "12": gli id dei match puntano all'annuncio con un prefisso. */
    private fun idAnnuncio(m: Match) = m.annuncioId.removePrefix("smart-").removePrefix("inf-")

    private fun giorno(a: AnnuncioTurno, tz: TimeZone) = a.dataTurno.toLocalDateTime(tz).date

    // --- "Il turno di sabato è coperto?" ---

    fun coperto(ctx: ContestoUtente, mieiAnnunci: List<AnnuncioTurno>?, testo: String, oggi: LocalDate, tz: TimeZone): String {
        // Il professionista chiede dei suoi turni: è la stessa domanda di "sono libero?".
        if (ctx.ruolo != UserRole.FARMACIA) return libero(ctx, testo, oggi, tz)
        if (mieiAnnunci == null) return NON_CARICATI
        val periodo = intervallo(testo, oggi, predefinito = 7)!!
        val turni = mieiAnnunci
            .filter { giorno(it, tz) in periodo && it.stato != AnnuncioStato.CANCELLATO }
            .filter { ctx.professioneCercata == null || it.professioneRichiesta == ctx.professioneCercata }
            .sortedBy { it.dataTurno }
        if (turni.isEmpty()) {
            return "Non hai turni pubblicati ${periodo.descrizione}. Per pubblicarne uno dimmi per esempio \"crea un turno sabato dalle 9 alle 13\"."
        }
        val righe = turni.take(5).map { a -> descriviCopertura(a, ctx, oggi, tz) }
        val scoperti = turni.count { a -> confermati(a, ctx).isEmpty() }
        val chiusura = if (scoperti > 0) {
            val chi = if (turni.any { it.professioneRichiesta == Professione.INFERMIERE } && turni.none { it.professioneRichiesta == Professione.FARMACISTA }) "un infermiere" else "un farmacista"
            " Se vuoi ti cerco io qualcuno: dimmi \"cercami $chi ${perRicerca(periodo)}\"."
        } else " Tutto coperto!"
        return righe.joinToString(" ") + (if (turni.size > 5) " E altri ${turni.size - 5} turni." else "") + chiusura
    }

    /** "a novembre", "nei prossimi 7 giorni" restano così; un giorno diventa "per sabato 10 ottobre". */
    private fun perRicerca(p: PeriodoRicerca): String =
        if (Regex("^(a|nei|nel|dal|la|questa) ").containsMatchIn(p.descrizione)) p.descrizione
        else "per " + p.descrizione.removePrefix("oggi, ").removePrefix("domani, ")

    private fun confermati(a: AnnuncioTurno, ctx: ContestoUtente) =
        ctx.matches.filter { idAnnuncio(it) == a.id && it.stato == MatchStato.CONFERMATO }

    private fun descriviCopertura(a: AnnuncioTurno, ctx: ContestoUtente, oggi: LocalDate, tz: TimeZone): String {
        val suoi = ctx.matches.filter { idAnnuncio(it) == a.id }
        val confermati = suoi.filter { it.stato == MatchStato.CONFERMATO }
        val daConfermare = suoi.filter { it.stato == MatchStato.ACCETTATO }
        val proposte = suoi.filter { it.stato == MatchStato.PROPOSTO }
        val quando = "${formattaGiorno(giorno(a, tz), oggi).replaceFirstChar { it.uppercase() }} ${a.oraInizio}-${a.oraFine}" +
            (if (ctx.professioneCercata == null && a.professioneRichiesta == Professione.INFERMIERE) " (infermiere)" else "")
        fun nomi(l: List<Match>) = l.mapNotNull { it.nomeControparte }.joinToString(", ").ifEmpty { null }
        return when {
            confermati.size >= a.quantitaMassima -> "$quando: coperto" + (nomi(confermati)?.let { " da $it" } ?: "") + "."
            confermati.isNotEmpty() -> "$quando: ${confermati.size} su ${a.quantitaMassima} confermati" +
                (nomi(confermati)?.let { " ($it)" } ?: "") + ", manca ancora qualcuno."
            daConfermare.isNotEmpty() -> "$quando: non ancora coperto, ma " +
                (nomi(daConfermare)?.let { "$it ha accettato" } ?: "${plurale(daConfermare.size, "persona ha", "persone hanno")} accettato") +
                ": devi confermare tu dalla Home."
            proposte.isNotEmpty() -> "$quando: non ancora coperto, ${plurale(proposte.size, "proposta", "proposte")} in attesa di risposta."
            else -> "$quando: ancora scoperto, nessuno si è fatto avanti."
        }
    }

    // --- "Quali turni ho pubblicato?" ---

    fun pubblicati(ctx: ContestoUtente, mieiAnnunci: List<AnnuncioTurno>?, testo: String, oggi: LocalDate, tz: TimeZone): String {
        val ruolo = ctx.ruolo ?: return "Accedi per vedere i turni che hai pubblicato."
        if (mieiAnnunci == null) return NON_CARICATI
        val periodo = intervallo(testo, oggi)
        val farmacia = ruolo == UserRole.FARMACIA
        val aperti = mieiAnnunci
            .filter { it.stato == AnnuncioStato.APERTO && giorno(it, tz) >= oggi && (periodo == null || giorno(it, tz) in periodo) }
            .filter { !farmacia || ctx.professioneCercata == null || it.professioneRichiesta == ctx.professioneCercata }
            .sortedBy { it.dataTurno }
        val cosa = if (farmacia) "turni" else "disponibilità"
        val dove = periodo?.let { " ${it.descrizione}" }.orEmpty()
        if (aperti.isEmpty()) {
            return "Non hai $cosa aperti$dove. " +
                if (farmacia) "Per pubblicarne uno dimmi \"crea un turno sabato dalle 9 alle 13\"."
                else "Per pubblicarne una dimmi \"crea una disponibilità sabato dalle 8 alle 20\"."
        }
        val elenco = aperti.take(5).joinToString("; ") { a ->
            buildString {
                append("${formattaGiorno(giorno(a, tz), oggi)} ${a.oraInizio}-${a.oraFine}")
                if (farmacia && ctx.professioneCercata == null && a.professioneRichiesta == Professione.INFERMIERE) {
                    append(" (${a.professioneRichiesta.etichetta.lowercase()})")
                }
                if (farmacia && a.quantitaMassima > 1) append(", ${a.postiOccupati} su ${a.quantitaMassima} posti presi")
            }
        }
        return "Hai ${plurale(aperti.size, if (farmacia) "turno aperto" else "disponibilità aperta", if (farmacia) "turni aperti" else "disponibilità aperte")}$dove: " +
            elenco + (if (aperti.size > 5) "; e altri ${aperti.size - 5}." else ".") +
            if (farmacia) " Per sapere chi li copre chiedimi \"il turno di sabato è coperto?\"." else ""
    }

    // --- "Sono libero sabato?" ---

    fun libero(ctx: ContestoUtente, testo: String, oggi: LocalDate, tz: TimeZone): String {
        if (ctx.ruolo == null) return "Accedi per vedere i tuoi impegni."
        val periodo = intervallo(testo, oggi)
            ?: return "Per quale giorno? Chiedimi per esempio \"sono libero sabato?\" o \"ho impegni la prossima settimana?\"."
        val impegni = ctx.matches
            .filter { it.stato == MatchStato.CONFERMATO || it.stato == MatchStato.ACCETTATO || it.stato == MatchStato.PROPOSTO }
            .mapNotNull { m -> m.dataTurno?.toLocalDateTime(tz)?.date?.takeIf { it in periodo }?.let { it to m } }
            .sortedBy { it.first }
        val unGiorno = periodo.dal == periodo.al
        if (impegni.none { it.second.stato == MatchStato.CONFERMATO || it.second.stato == MatchStato.ACCETTATO }) {
            val proposte = impegni.count { it.second.stato == MatchStato.PROPOSTO }
            return "Sì, ${periodo.descrizione} sei ${if (unGiorno) "libero" else "senza turni"}." +
                (if (proposte > 0) " Hai però ${plurale(proposte, "proposta", "proposte")} ancora da definire per quei giorni." else "") +
                " Vuoi vedere i turni aperti? Chiedimi \"ci sono turni ${periodo.descrizione}?\"."
        }
        val elenco = impegni.filter { it.second.stato != MatchStato.PROPOSTO }.take(4).joinToString("; ") { (d, m) ->
            buildString {
                if (!unGiorno) append("${formattaGiorno(d, oggi)} ")
                append(m.nomeControparte?.let { "con $it" } ?: "un turno")
                append(if (m.stato == MatchStato.CONFERMATO) " (confermato)" else " (in attesa di conferma)")
            }
        }
        return (if (unGiorno) "No, ${periodo.descrizione} hai già un impegno: " else "${periodo.descrizione.replaceFirstChar { it.uppercase() }} hai questi impegni: ") +
            "$elenco."
    }

    /** Turni aperti delle controparti ristretti al giorno o periodo della domanda (nessuno detto = tutti). */
    fun nelPeriodo(annunci: List<AnnuncioTurno>, testo: String, oggi: LocalDate, tz: TimeZone): Pair<List<AnnuncioTurno>, String?> {
        val periodo = intervallo(testo, oggi) ?: return annunci to null
        return annunci.filter { giorno(it, tz) in periodo } to periodo.descrizione
    }

    private const val NON_CARICATI = "Non riesco a caricare i tuoi turni in questo momento: controlla la connessione e riprova."
}
